// ingestDecider — the STRICT decide/evolve decider for the source-ingest flow
// (determinism migration M9.1; the ingest analogue of chatDecider.ts's chat
// pilot M1).
//
// Ingest is structurally SIMPLER than chat: there is no live stream and no
// server-side long-running re-entry. Extraction (PDF/DOCX/PPTX → text) runs
// SYNCHRONOUSLY inside the `addSource` port round-trip, so from the UI's
// vantage there is exactly ONE port resolution per file (done or failed). What
// ingest adds is BATCH semantics (N files, sequential) and CONCURRENT batches
// racing a shared progress list — today guarded imperatively by an `ingestSeq`
// ref that hands each batch a disjoint id range. Before M9.1 the batch list
// advanced through ~5 ad-hoc `setIngest`/`setDropIndication` closures inside
// IngestHost's async loop and window drag listeners — port outcomes writing
// React state directly, the id-disjointness invariant living in an imperative
// ref instead of the fold. This module concentrates ALL of that into one pure
// decider so "batch A lands vs batch B lands vs a stale settle, in which
// order?" becomes a value-level test (M9.4), and the id-disjointness guard
// becomes STRUCTURAL (matched by id in `evolveIngest`, not enforced by a ref).
//
// Shape (ADR-1, decisions.jsonl bd36663e; mirrors chatDecider.ts):
//   - `decideIngest(state, command): Result<IngestDecision, IngestError>` — the
//     validating half. The one refusal ADR-I1 calls out is `settle_after_clear`
//     (a port settle arriving for an item the user already cleared): today's
//     silent `ingestSeq` disjoint-id / non-pending no-op becomes an EXPLICIT,
//     testable error the shell swallows user-silently. On success it yields an
//     `IngestDecision`: the `IngestEvent[]` that happened plus the
//     `IngestEffect[]` the SHELL interprets. Effects are DATA — decide runs none.
//   - `evolveIngest(state, event): IngestState` — the total half. State × Event
//     → State, no Result, no throw; it reuses the `app/ingest.ts` folds
//     (mkIngestPending / settleDone / settleFailed / clearSettled) as arm bodies.
//
// Purity (dev-ts, pure-core/impure-shell): no I/O, no clock, no id-minting, no
// FileReader, no React. The shell mints each batch's disjoint IngestId range
// (its `ingestSeq` ref), reads each `File`'s bytes (FileReader), and calls the
// port; the ids ride the command payload and the raw `File` handles ride the
// `run_ingest_batch` effect — the decider only folds and routes them. Per the
// pointer-not-payload discipline (IDD 6f32fe1a): NO `IngestEvent` payload
// carries file bytes or extract text — `files_queued` carries only {id,
// filename}, `source_landed` only the `AddedSource` pointer. Raw `File` handles
// appear ONLY on the command and the `run_ingest_batch` effect (transient shell
// instructions), never in folded state.
//
// ADR-I2 (single pending state): extraction-in-flight is ONE `pending` state.
// If a durable async extraction PM ever lands (commands.ts flags the sync-in-
// request shortcut), the UI would see `uploading → extracting → settled` and a
// mid-state command would slot in additively here — not modeled now (a
// speculative event type has removal cost ≈ ∞).
// ADR-I4 (library reload): `batch_settled` emits a `reload_library` EFFECT the
// shell runs. This does NOT fix the concurrent-batch last-writer-wins clobber
// on the SHARED `library` cell (AppShellHost Group P) — that is the deferred
// client-event-fold substrate's job. Routing the reload through an effect lets
// a later serializer land without touching the decider.
// ADR-I6 (live-failure ≠ replay-degradation): a failed extraction returns
// BEFORE any `resource_ingested` event is appended, so there is no aggregate to
// freeze — a `file_failed` here is a batch-entry toast, NOT the aggregate-scoped
// replay degradation of an already-persisted event. Do not wire the two together.
//
// Retry/cancel (ADR-I5, determinism M9 step) are the decider's first
// post-migration extension, added additively as distinct commands (never a flag
// on another command): `retry_file` + `cancel_batch`, with events `file_retried`
// + `batch_cancelled`. Retry re-runs `addSource` for ONE failed file whose `File`
// the failed state RETAINS (accepted memory cost, ADR-I5) — the decider reads
// that handle off state to route it into a single-file run effect, never reading
// its bytes. Cancel is SOFT: it emits a `signal_cancel` effect the shell reads to
// stop scheduling further files BETWEEN round-trips; the in-flight `addSource` is
// allowed to finish (its landing/failure still settles), already-landed sources
// are kept, and the never-started ids ride the coda's `discardedIds` so they do
// not linger as ghost pending rows. The cancellation TOKEN + loop live in the
// shell (impure); the decider only records the intent and the `cancelling` fact.

import { exhaustSwitchCase, Result } from "@bilda/utils";
import type { ResourceRef } from "../components/Navigation/types.js";
import type { ExternalError } from "./externalError.js";
import type { HostNotice } from "./frame.js";
import {
    appendRejected,
    classifyQueuedFile,
    clearSettled,
    type IngestFile,
    type IngestId,
    type IngestRejection,
    mkIngestPending,
    retryPending,
    settleDone,
    settleFailed,
} from "./ingest.js";
import type { AddedSource } from "./ports.js";

// A file the shell has already minted an IngestId for and holds the raw `File`
// handle of. The decider routes {id, filename} into state (pointer-only) and
// {id, file} into the run effect — it never reads the bytes.
export type QueuedFile = {
    readonly id: IngestId;
    readonly filename: string;
    // The raw byte size the shell read off the `File` at mint time (its impure
    // edge). Carried as a plain value so the decider validates size (Win 3)
    // WITHOUT touching the `File` handle — the decider still never reads bytes.
    readonly size: number;
    readonly file: File;
};

// Whether an OS file drag currently hovers the window (arms the drop overlay).
export type DropIndication = "idle" | "over";

// === IngestState — the product the decider owns ==============================
// `batch` is the running batch's per-file progress (the `app/ingest.ts` sum,
// unchanged). `dropIndication` + `dragDepth` are the drag-hover arm absorbed
// out of IngestHost's `useState`/`useRef` (dragDepth was a ref only to dodge
// re-renders — now a folded fact). Concurrent batches share `batch`; their id
// ranges are disjoint (shell-minted), so a settle only ever matches its own
// batch's pending entry — the disjointness is now expressed in the fold.
export type IngestState = {
    readonly batch: readonly IngestFile[];
    readonly dropIndication: DropIndication;
    readonly dragDepth: number;
    // A soft-cancel is underway (ADR-I5): set when `batch_cancelled` folds,
    // cleared when the batch coda folds (`batch_cleared`). The folded MIRROR of
    // the shell's cancellation token — the loop reads the token; this makes the
    // cancel intent a testable, folded fact and lets a second `cancel_batch`
    // refuse (`cancel_not_running`) instead of re-firing.
    readonly cancelling: boolean;
};

export const initialIngestState: IngestState = {
    batch: [],
    dropIndication: "idle",
    dragDepth: 0,
    cancelling: false,
};

// === IngestCommand — what was MEANT (the decide inputs) ======================
// User gestures (drag arm, drop, pick) and world outcomes re-entering as each
// file's port round-trip resolves (file_landed / file_failed) plus the batch
// coda (batch_settled). Every IngestId is already minted onto the payload by
// the shell's `ingestSeq` ref.

// An OS file drag entered the window (arms the overlay).
export type DragEntered = { readonly type: "drag_entered" };
// An OS file drag left (disarms when the enter/leave depth returns to zero).
export type DragDeparted = { readonly type: "drag_departed" };
// The author dropped file(s) anywhere on the window (owner decision B5). Resets
// the drag arm AND queues the batch. `items` carry shell-minted ids + raw handles.
export type DroppedFiles = {
    readonly type: "dropped_files";
    readonly items: readonly QueuedFile[];
};
// The author picked file(s) through the hidden `<input>` (no drag to reset).
export type PickedFiles = {
    readonly type: "picked_files";
    readonly items: readonly QueuedFile[];
};
// `ports.addSource` resolved OK for one file: the new Source pointer landed.
export type FileLanded = {
    readonly type: "file_landed";
    readonly id: IngestId;
    readonly source: AddedSource;
};
// `ports.addSource` resolved ERR for one file (or the local read failed). The
// typed `error` lands in state (pointer discipline); `message` is the shell's
// already-localized one-line string for the toast (the decider owns no i18n
// instance — mirrors chat's `TurnRefused.message`). `file` is the raw upload
// handle RETAINED on the failed state so `retry_file` re-runs WITHOUT re-picking
// (ADR-I5); it rides the command (a transient shell instruction) and the
// `source_ingest_failed` event, but NEVER a recorded intent essence
// (`intentDispatch`'s `ingestFiles` keeps only {id, filename}).
export type FileFailed = {
    readonly type: "file_failed";
    readonly id: IngestId;
    readonly filename: string;
    readonly error: ExternalError;
    readonly message: string;
    readonly file: File;
};
// The batch's sequential run finished. Landing a source no longer NAVIGATES
// (F-B / DB3 — calm ingest: tabs are working memory, so opening is an explicit
// gesture, never an ingest side effect); the confirmation is the landing hero's
// added-source chip instead. `discardedIds` are the queued ids the run loop
// NEVER reached — non-empty only after a soft-cancel broke the loop between
// files (ADR-I5); the coda drops these still-pending, never-started entries so
// they don't linger as ghost "Adding…" rows. Empty for a batch that ran to
// completion.
export type BatchSettled = {
    readonly type: "batch_settled";
    readonly discardedIds: readonly IngestId[];
};
// Re-run one FAILED file's upload (ADR-I5). Distinct command per intent — a
// retry is its own gesture, never a flag on another command. Carries only the
// id; the decider reads the retained `File` off the failed item in state and
// routes it into the run effect (it never reads the bytes).
export type RetryFile = {
    readonly type: "retry_file";
    readonly id: IngestId;
};
// Soft-cancel the in-flight batch (ADR-I5): stop scheduling further files. The
// currently in-flight `addSource` is allowed to FINISH (its landing/failure
// still settles); already-landed sources are kept. Distinct command per intent
// (never a flag). Carries nothing — cancel is batch-wide.
export type CancelBatch = { readonly type: "cancel_batch" };

export type IngestCommand =
    | DragEntered
    | DragDeparted
    | DroppedFiles
    | PickedFiles
    | FileLanded
    | FileFailed
    | BatchSettled
    | RetryFile
    | CancelBatch;

// === IngestEvent — what HAPPENED (the evolve inputs) =========================
// Past-tense facts. `evolveIngest` is total over this sum. `files_queued`
// carries ONLY {id, filename} (pointer discipline — no bytes reach state).

// The drag arm advanced (overlay shows).
export type DragIndicated = { readonly type: "drag_indicated" };
// The drag arm receded one step (overlay hides when depth hits zero).
export type DragReceded = { readonly type: "drag_receded" };
// The drag arm was reset to zero (a drop landed).
export type DragReset = { readonly type: "drag_reset" };
// Pending entries were appended to the batch, one per queued file.
export type FilesQueued = {
    readonly type: "files_queued";
    readonly entries: readonly {
        readonly id: IngestId;
        readonly filename: string;
    }[];
};
// Files a drop/pick refused BEFORE upload (Win 2/3): wrong type, or too large.
// Pointer-only ({id, filename, rejection}) — no bytes, no `File` (a rejection
// never uploads and is never retried). Folded into the batch as `rejected`
// entries so the Sources ingest surface shows each with its specific reason.
export type FilesRejected = {
    readonly type: "files_rejected";
    readonly entries: readonly {
        readonly id: IngestId;
        readonly filename: string;
        readonly rejection: IngestRejection;
    }[];
};
// One pending entry settled `done` with its Source pointer.
export type SourceLanded = {
    readonly type: "source_landed";
    readonly id: IngestId;
    readonly source: AddedSource;
};
// One pending entry settled `failed` with its typed error. `file` is the
// retained upload handle the failed state holds for `retry_file` (ADR-I5) —
// present on the fold input so `evolveIngest` can store it; NEVER recorded (an
// event is not a tape essence, and `IngestFile` is UI-only state).
export type SourceIngestFailed = {
    readonly type: "source_ingest_failed";
    readonly id: IngestId;
    readonly error: ExternalError;
    readonly file: File;
};
// The batch coda: DONE entries dropped from the batch list; `pending` and
// `failed` kept (ADR-I5 — failures persist for retry). `discardedIds` are the
// never-started ids a soft-cancel skipped, additionally dropped here.
export type BatchCleared = {
    readonly type: "batch_cleared";
    readonly discardedIds: readonly IngestId[];
};
// One failed entry was re-armed to `pending` for another attempt (ADR-I5).
export type FileRetried = {
    readonly type: "file_retried";
    readonly id: IngestId;
};
// The in-flight batch was soft-cancelled (ADR-I5): marks `cancelling` so the
// coda and a second cancel can see it. The shell separately flips its token.
export type BatchCancelled = { readonly type: "batch_cancelled" };

export type IngestEvent =
    | DragIndicated
    | DragReceded
    | DragReset
    | FilesQueued
    | FilesRejected
    | SourceLanded
    | SourceIngestFailed
    | BatchCleared
    | FileRetried
    | BatchCancelled;

// === IngestError — explicit refusals (ADR-I1) ===============================
// A port settle arriving for an item that is no longer pending — the user
// cleared it (or the id was never queued). Pre-M9 this was a silent
// settleDone/settleFailed no-op; now a named, testable refusal the shell
// swallows user-silently (semantics preserved).
export type SettleAfterClear = {
    readonly type: "settle_after_clear";
    readonly id: IngestId;
};
// `retry_file` for an id that is not a FAILED entry (unknown, still pending, or
// already done) — retry only re-runs a failure (ADR-I5).
export type RetryNotFailed = {
    readonly type: "retry_not_failed";
    readonly id: IngestId;
};
// `cancel_batch` with nothing to cancel: no file is in flight, or a cancel is
// already underway (ADR-I5).
export type CancelNotRunning = { readonly type: "cancel_not_running" };
export type IngestError = SettleAfterClear | RetryNotFailed | CancelNotRunning;

// === IngestEffect — DATA out of decide, interpreted by the shell =============
// The decider names WHAT to do; the shell (impure) reads the files, calls the
// ports, opens the tab, surfaces the toast.
export type RunIngestBatchEffect = {
    readonly type: "run_ingest_batch";
    readonly items: readonly { readonly id: IngestId; readonly file: File }[];
};
export type ReloadLibraryEffect = { readonly type: "reload_library" };
// Open a resource ref in a tab. RETAINED as a latent shell capability but no
// longer auto-produced by ingest (F-B / DB3 — calm ingest never navigates on
// landing). The shell still interprets it, so an explicit "open this source"
// gesture can route through the same door if a future path wants it.
export type OpenRefEffect = {
    readonly type: "open_ref";
    readonly ref: ResourceRef;
};
export type NotifyEffect = {
    readonly type: "notify";
    readonly notice: HostNotice;
};
// Soft-cancel marker (ADR-I5): the shell flips its cancellation token so the
// running batch loop stops scheduling further files between round-trips. The
// token + the loop live in the shell; the decider only NAMES the cancel.
export type SignalCancelEffect = { readonly type: "signal_cancel" };

export type IngestEffect =
    | RunIngestBatchEffect
    | ReloadLibraryEffect
    | OpenRefEffect
    | NotifyEffect
    | SignalCancelEffect;

// A successful decision: the facts to fold plus the effects to interpret.
export type IngestDecision = {
    readonly events: readonly IngestEvent[];
    readonly effects: readonly IngestEffect[];
};

// True iff an entry with this id is still `pending` (the only settle-able state).
const hasPending = (state: IngestState, id: IngestId): boolean =>
    state.batch.some((f) => f.type === "pending" && f.id === id);

// A queued file split into the two projections the decision routes: the
// pointer-only {id, filename} that lands in state, and the {id, file} raw
// handle that rides the run effect.
const queuedFacts = (
    items: readonly QueuedFile[],
): {
    readonly entries: readonly {
        readonly id: IngestId;
        readonly filename: string;
    }[];
    readonly runItems: readonly {
        readonly id: IngestId;
        readonly file: File;
    }[];
} => ({
    entries: items.map(({ id, filename }) => ({ id, filename })),
    runItems: items.map(({ id, file }) => ({ id, file })),
});

// Split a drop/pick into the files that pass the pre-upload gate (Win 2/3) and
// the ones refused up front. `classifyQueuedFile` is the pure verdict; the
// rejected projection is pointer-only (no `File`) — it lands in state, never
// runs.
const partitionQueued = (
    items: readonly QueuedFile[],
): {
    readonly accepted: readonly QueuedFile[];
    readonly rejected: readonly {
        readonly id: IngestId;
        readonly filename: string;
        readonly rejection: IngestRejection;
    }[];
} => {
    const verdicts = items.map((it) => ({
        it,
        rejection: classifyQueuedFile(it.filename, it.size),
    }));
    return {
        accepted: verdicts.filter((v) => v.rejection === null).map((v) => v.it),
        rejected: verdicts.flatMap((v) =>
            v.rejection === null
                ? []
                : [
                      {
                          id: v.it.id,
                          filename: v.it.filename,
                          rejection: v.rejection,
                      },
                  ],
        ),
    };
};

// === decideIngest — the validating half =====================================
export const decideIngest = (
    state: IngestState,
    command: IngestCommand,
): Result.Result<IngestDecision, IngestError> => {
    switch (command.type) {
        case "drag_entered":
            return Result.ok({
                events: [{ type: "drag_indicated" }],
                effects: [],
            });
        case "drag_departed":
            return Result.ok({
                events: [{ type: "drag_receded" }],
                effects: [],
            });
        case "dropped_files": {
            // Win 2/3: split off files refused up front (wrong type / too
            // large) — they fold in as `rejected`, never upload. A drop always
            // resets the drag arm (even an empty/all-rejected drop); it queues
            // + runs only the accepted files.
            const { accepted, rejected } = partitionQueued(command.items);
            const { entries, runItems } = queuedFacts(accepted);
            const queueEvents: readonly IngestEvent[] =
                entries.length === 0 ? [] : [{ type: "files_queued", entries }];
            const rejectEvents: readonly IngestEvent[] =
                rejected.length === 0
                    ? []
                    : [{ type: "files_rejected", entries: rejected }];
            return Result.ok({
                events: [
                    { type: "drag_reset" },
                    ...queueEvents,
                    ...rejectEvents,
                ],
                effects:
                    runItems.length === 0
                        ? []
                        : [{ type: "run_ingest_batch", items: runItems }],
            });
        }
        case "picked_files": {
            // The picker's `<input accept>` already filters by type, but a
            // determined pick (or a future non-filtering caller) can still land
            // an oversized/odd file — validate the same way (Win 2/3).
            const { accepted, rejected } = partitionQueued(command.items);
            const { entries, runItems } = queuedFacts(accepted);
            const queueEvents: readonly IngestEvent[] =
                entries.length === 0 ? [] : [{ type: "files_queued", entries }];
            const rejectEvents: readonly IngestEvent[] =
                rejected.length === 0
                    ? []
                    : [{ type: "files_rejected", entries: rejected }];
            return Result.ok({
                events: [...queueEvents, ...rejectEvents],
                effects:
                    runItems.length === 0
                        ? []
                        : [{ type: "run_ingest_batch", items: runItems }],
            });
        }
        case "file_landed":
            // ADR-I1: settling an already-cleared (or never-queued) id is the
            // one refusal — a tested assertion, not a silent no-op.
            if (!hasPending(state, command.id))
                return Result.err({
                    type: "settle_after_clear",
                    id: command.id,
                });
            return Result.ok({
                events: [
                    {
                        type: "source_landed",
                        id: command.id,
                        source: command.source,
                    },
                ],
                effects: [],
            });
        case "file_failed":
            if (!hasPending(state, command.id))
                return Result.err({
                    type: "settle_after_clear",
                    id: command.id,
                });
            return Result.ok({
                events: [
                    {
                        type: "source_ingest_failed",
                        id: command.id,
                        error: command.error,
                        file: command.file,
                    },
                ],
                effects: [
                    {
                        type: "notify",
                        notice: {
                            type: "ingest-failed",
                            filename: command.filename,
                            message: command.message,
                        },
                    },
                ],
            });
        case "batch_settled":
            // ADR-I4: reload the library (shell dedupes) — and nothing else.
            // Calm ingest (F-B / DB3): the coda no longer opens the last-added
            // source's tab; the landing hero's added-source chip is the calm
            // confirmation instead (opening is an explicit gesture the author
            // makes from that chip). `batch_cleared` drops the DONE entries (a
            // batch started meanwhile keeps its pending; failures persist for
            // retry, ADR-I5) plus any soft-cancel `discardedIds`.
            return Result.ok({
                events: [
                    {
                        type: "batch_cleared",
                        discardedIds: command.discardedIds,
                    },
                ],
                effects: [{ type: "reload_library" }],
            });
        case "retry_file": {
            // ADR-I5: retry re-runs ONE failed file. Refuse anything that is
            // not a failed entry (unknown, still pending, already done). Read
            // the retained `File` off the failed item (a pure value read — never
            // its bytes) and route it into a single-file run effect; the event
            // re-arms the entry to `pending`.
            const item = state.batch.find((f) => f.id === command.id);
            if (item === undefined || item.type !== "failed")
                return Result.err({
                    type: "retry_not_failed",
                    id: command.id,
                });
            return Result.ok({
                events: [{ type: "file_retried", id: command.id }],
                effects: [
                    {
                        type: "run_ingest_batch",
                        items: [{ id: item.id, file: item.file }],
                    },
                ],
            });
        }
        case "cancel_batch":
            // ADR-I5: soft-cancel. Refuse when nothing is in flight or a cancel
            // is already underway (`cancel_not_running`). Otherwise mark
            // `cancelling` and emit the `signal_cancel` marker the shell reads.
            if (
                state.cancelling ||
                !state.batch.some((f) => f.type === "pending")
            )
                return Result.err({ type: "cancel_not_running" });
            return Result.ok({
                events: [{ type: "batch_cancelled" }],
                effects: [{ type: "signal_cancel" }],
            });
        default:
            return exhaustSwitchCase(command);
    }
};

// === evolveIngest — the total half ==========================================
// State × Event → State. Trusts payloads; no Result, no throw. The batch arms
// reuse the `app/ingest.ts` folds verbatim (settleDone/settleFailed match by id
// and no-op a non-pending entry — the structural id-disjointness guard).
export const evolveIngest = (
    state: IngestState,
    event: IngestEvent,
): IngestState => {
    switch (event.type) {
        case "drag_indicated":
            return {
                ...state,
                dragDepth: state.dragDepth + 1,
                dropIndication: "over",
            };
        case "drag_receded": {
            const depth = Math.max(0, state.dragDepth - 1);
            return {
                ...state,
                dragDepth: depth,
                dropIndication: depth === 0 ? "idle" : state.dropIndication,
            };
        }
        case "drag_reset":
            return { ...state, dragDepth: 0, dropIndication: "idle" };
        case "files_queued":
            return {
                ...state,
                batch: [
                    ...state.batch,
                    ...event.entries.map((e) =>
                        mkIngestPending(e.id, e.filename),
                    ),
                ],
            };
        case "files_rejected":
            return {
                ...state,
                batch: appendRejected(state.batch, event.entries),
            };
        case "source_landed":
            return {
                ...state,
                batch: settleDone(state.batch, event.id, event.source),
            };
        case "source_ingest_failed":
            return {
                ...state,
                batch: settleFailed(
                    state.batch,
                    event.id,
                    event.error,
                    event.file,
                ),
            };
        case "batch_cleared":
            return {
                ...state,
                cancelling: false,
                // Drop DONE entries (clearSettled) AND the soft-cancel's
                // never-started ids; pending/failed otherwise survive.
                batch: clearSettled(state.batch).filter(
                    (f) => !event.discardedIds.includes(f.id),
                ),
            };
        case "file_retried":
            return {
                ...state,
                batch: retryPending(state.batch, event.id),
            };
        case "batch_cancelled":
            return { ...state, cancelling: true };
        default:
            return exhaustSwitchCase(event);
    }
};

// Fold a whole decision's events onto state — the shell's commit step.
export const evolveIngestAll = (
    state: IngestState,
    events: readonly IngestEvent[],
): IngestState => events.reduce(evolveIngest, state);
